import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { compressImageFile } from "../lib/imageCompress";
import { coverSrc } from "../lib/images";

/**
 * Reusable image field: shows a preview, a URL text input, an upload button,
 * and supports drag-and-drop straight onto the preview box.
 * value = current image url or data URL; onChange(newValue).
 */
export default function ImageField({ value, onChange, testid = "image", aspect = "portrait" }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    try {
      const dataUrl = await compressImageFile(file);
      onChange(dataUrl);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const boxAspect = aspect === "wide" ? "w-28 h-20" : aspect === "square" ? "w-20 h-20" : "w-16 h-20";

  return (
    <div className="flex gap-3 items-start">
      <div
        data-testid={`${testid}-dropzone`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative shrink-0 ${boxAspect} border overflow-hidden cursor-pointer flex items-center justify-center transition-colors ${
          dragging ? "border-crimson bg-crimson/10" : "border-line bg-ink"
        }`}
        title="Click or drop an image here"
      >
        {value ? (
          <img src={coverSrc(value, "detail")} alt="preview" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        ) : (
          <UploadCloud className="w-5 h-5 text-vellum-faint" />
        )}
        {dragging && (
          <div className="absolute inset-0 flex items-center justify-center bg-ink/70">
            <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-crimson">Drop</span>
          </div>
        )}
      </div>
      <div className="flex-1">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-url`}
          className="w-full bg-transparent border border-line px-4 py-3 text-vellum text-sm focus:border-crimson outline-none transition-colors"
          placeholder="Paste an image URL…"
        />
        <label
          data-testid={`${testid}-upload-label`}
          className="mt-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-line text-vellum-muted hover:border-crimson hover:text-crimson transition-colors cursor-pointer"
        >
          <UploadCloud className="w-3.5 h-3.5" /> Upload or drop
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`${testid}-upload`}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }}
          />
        </label>
      </div>
    </div>
  );
}
